import { zodResolver } from "@hookform/resolvers/zod";
import type React from "react";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trackAnalytics } from "@/lib/analytics";
import { bookingDetailsSchema } from "@/lib/validation";
import { cn, formatCurrency } from "@/lib/utils";
import type { StepProps } from "@/components/booking/steps/types";
import { StepFrame } from "@/components/booking/steps/StepFrame";

type BookingDetailsForm = z.infer<typeof bookingDetailsSchema>;

const timeSlots = ["AM", "PM"];

export function BookingDetailsStep({ state, isLocked, updateState, goNext, goBack }: StepProps) {
  const form = useForm<BookingDetailsForm>({
    resolver: zodResolver(bookingDetailsSchema),
    defaultValues: { ...state.bookingDetails, postcode: state.postcode },
  });
  const isCompanyBooking = form.watch("isCompanyBooking");

  function onSubmit(values: BookingDetailsForm) {
    updateState({ bookingDetails: { ...values, addressLine2: values.addressLine2 ?? "", county: values.county ?? "", companyName: values.companyName ?? "", driverNotes: values.driverNotes ?? "" } });
    trackAnalytics("booking details completed", { service: state.service });
    goNext();
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <StepFrame
        title="Booking details"
        description="Choose an available weekday slot and enter the contact and job details for the driver."
        canBack
        onBack={goBack}
        isReadOnly={isLocked}
        mobileSummary={state.pricingResult ? `Current total ${formatCurrency(state.pricingResult.totalPayable)}` : undefined}
      >
        <section className="space-y-4 rounded-lg border-2 border-accent/10 bg-white p-4">
          <h3 className="text-sm font-black uppercase text-accent">Date and time</h3>
          <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_10rem]">
          <Field label="Delivery date" error={form.formState.errors.date?.message}>
            <Controller
              control={form.control}
              name="date"
              render={({ field }) => (
                <Calendar id="date" value={field.value} onChange={field.onChange} readOnly={isLocked} disabled={isLocked} />
              )}
            />
          </Field>
          <Field label="Time slot" error={form.formState.errors.timeSlot?.message}>
            <Controller
              control={form.control}
              name="timeSlot"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange} disabled={isLocked}>
                  <SelectTrigger className="h-12 rounded-md bg-white" aria-readonly={isLocked}>
                    <SelectValue placeholder="Choose a time slot" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((slot) => (
                      <SelectItem key={slot} value={slot}>
                        {slot}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          </div>
        </section>
        <section className="mt-4 space-y-4 rounded-lg border-2 border-accent/10 bg-white p-4">
          <h3 className="text-sm font-black uppercase text-accent">Contact</h3>
          <div className="grid gap-4 md:grid-cols-2">
          <Field label="Full name" error={form.formState.errors.fullName?.message}>
            <Input {...form.register("fullName")} autoComplete="name" readOnly={isLocked} disabled={isLocked} />
          </Field>
          <Field label="Email address" error={form.formState.errors.email?.message}>
            <Input type="email" {...form.register("email")} autoComplete="email" readOnly={isLocked} disabled={isLocked} />
          </Field>
          <Field label="Phone number" error={form.formState.errors.phone?.message}>
            <Input type="tel" {...form.register("phone")} autoComplete="tel" readOnly={isLocked} disabled={isLocked} />
          </Field>
          </div>
          <div className="space-y-4">
            <Label
              className={cn(
                "flex items-center gap-3 text-sm font-medium",
                isLocked && "cursor-not-allowed opacity-50",
              )}
            >
              <Checkbox
                checked={isCompanyBooking}
                onChange={(event) => form.setValue("isCompanyBooking", event.target.checked, { shouldValidate: true })}
                readOnly={isLocked}
                disabled={isLocked}
              />
              Are you booking on behalf of a company?
            </Label>
            {isCompanyBooking ? (
              <Field label="Company name" error={form.formState.errors.companyName?.message}>
                <Input {...form.register("companyName")} autoComplete="organization" readOnly={isLocked} disabled={isLocked} />
              </Field>
            ) : null}
          </div>
        </section>
        <section className="mt-4 space-y-4 rounded-lg border-2 border-accent/10 bg-white p-4">
          <h3 className="text-sm font-black uppercase text-accent">Job address</h3>
          <div className="grid gap-4 md:grid-cols-2">
          <Field label="Postcode" error={form.formState.errors.postcode?.message}>
            <Input {...form.register("postcode")} autoComplete="postal-code" readOnly={isLocked} disabled={isLocked} />
          </Field>
          <Field label="Address line 1" error={form.formState.errors.addressLine1?.message}>
            <Input {...form.register("addressLine1")} autoComplete="address-line1" readOnly={isLocked} disabled={isLocked} />
          </Field>
          <Field label="Address line 2" error={form.formState.errors.addressLine2?.message}>
            <Input {...form.register("addressLine2")} autoComplete="address-line2" readOnly={isLocked} disabled={isLocked} />
          </Field>
          <Field label="Town/city" error={form.formState.errors.townCity?.message}>
            <Input {...form.register("townCity")} autoComplete="address-level2" readOnly={isLocked} disabled={isLocked} />
          </Field>
          <Field label="County" error={form.formState.errors.county?.message}>
            <Input {...form.register("county")} autoComplete="address-level1" readOnly={isLocked} disabled={isLocked} />
          </Field>
          </div>
        </section>
        <section className="mt-4 space-y-4 rounded-lg border-2 border-accent/10 bg-white p-4">
          <h3 className="text-sm font-black uppercase text-accent">Access notes</h3>
          <Field label="Additional delivery details for the driver" error={form.formState.errors.driverNotes?.message}>
            <textarea
              className="min-h-28 w-full rounded-lg border-2 border-input bg-white px-4 py-3 text-sm font-medium shadow-inner shadow-accent/5 placeholder:text-muted-foreground focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Narrow access, gate code, where to place skip, customer not home, etc."
              {...form.register("driverNotes")}
              readOnly={isLocked}
              disabled={isLocked}
            />
          </Field>
        </section>
      </StepFrame>
    </form>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
      {error ? <p className="text-sm font-semibold text-destructive">{error}</p> : null}
    </div>
  );
}
